import type { InvoiceTrackerKind } from '@/lib/types';

/**
 * HOW A PLANNED ROW'S `kind` IS WORDED, IN ONE PLACE.
 *
 * Three surfaces label this value — the plan dialog's read-only view on an
 * edit, the Accounts dashboard's Raise button and row, and the Future Billables
 * tracker's column and filter — and the backend's Google Sheet mirror renders a
 * fourth copy of the same three words. They have to agree: a row reading
 * "Invoice" on one screen and "An invoice" on another is survivable, but a row
 * reading "Not known yet" on one and "Quote" on another is a plan that appears
 * to have been decided.
 *
 * ── WHY A `Record` AND NOT A FUNCTION WITH A SWITCH ───────────────────────
 *
 * ⛔ **BECAUSE A FOURTH MEMBER MUST NOT COMPILE.** An exhaustive `Record` over
 * the union fails to build the day somebody adds one; a `switch` with a
 * `default`, or a two-arm ternary, silently mislabels it. That is not
 * hypothetical here: migration 161 added `'unknown'` to a union that three
 * ternaries were branching on, and **`tsc` found none of them** — a ternary is
 * not an exhaustiveness check, so every one of those sites went on compiling
 * while labelling an undecided plan as an invoice.
 *
 * ⚠️ **THE LABEL IS NOT THE VALUE.** `'unknown'` is the wire value and
 * "Not known yet" is the word. Never send a label, never match on one, and
 * never derive one by capitalising a slug — this codebase has been bitten three
 * times by reading an admin-editable name as a flag, and the discipline is the
 * same even where the vocabulary is a fixed enum.
 */
export const KIND_LABELS: Record<InvoiceTrackerKind, string> = {
  quote: 'A quote',
  invoice: 'An invoice',
  unknown: 'Not known yet',
};

/**
 * The same three, as a bare noun for a column cell, a filter row or a button.
 *
 * ⚠️ **TWO VOCABULARIES ON PURPOSE, and they are not interchangeable.**
 * {@link KIND_LABELS} answers "what is being planned here?" beside a control,
 * where the article reads naturally; this answers "what is this row?" in a
 * table cell, where `An invoice` reads as a sentence fragment and will not
 * align with its neighbours.
 *
 * ⚠️ **`unknown` KEEPS THE SAME WORDS IN BOTH.** There is no articleless form
 * of "Not known yet" worth inventing, and two near-identical phrasings of the
 * one state a reader is most likely to query is exactly how somebody comes to
 * believe they are two different things. **A cell must never render it blank**
 * either: an empty cell reads as data that failed to load, where this is
 * somebody's actual answer.
 */
export const KIND_NOUNS: Record<InvoiceTrackerKind, string> = {
  quote: 'Quote',
  invoice: 'Invoice',
  unknown: 'Not known yet',
};

/**
 * Which document a plan can be raised as.
 *
 * `'unknown'` yields BOTH, which is the entire behavioural consequence of
 * migration 161 on this client: the person picks at the moment they raise,
 * because that is the moment the answer exists. Everything else yields exactly
 * one, so the Raise control stays a single button on a stated plan.
 *
 * ⚠️ **The server agrees rather than merely permitting this** — `markRaised`
 * exempts `'unknown'` from its kind check and then SETTLES the row to whichever
 * document was named. So the choice made here is recorded, not discarded.
 */
export function raisableAs(kind: InvoiceTrackerKind): readonly ('quote' | 'invoice')[] {
  if (kind === 'quote') return ['quote'];
  if (kind === 'invoice') return ['invoice'];
  return ['quote', 'invoice'];
}
